import {
  formatDocDateTime,
  formatLocalDateOnly,
  isDateOnlyString,
} from '../views/htmlDocumentUtils';

export function parsePaymentAmounts(payment) {
  const amount = parseFloat(payment?.amount || 0);
  const refunded = parseFloat(payment?.refunded_amount || 0);
  const net = amount - refunded;
  const isRefunded = payment?.is_refunded === true;
  const isPartialRefund = refunded > 0 && refunded < amount;
  const isFullRefund = refunded > 0 && refunded >= amount;
  return { amount, refunded, net, isRefunded, isPartialRefund, isFullRefund };
}

export function sortPaymentsForLedger(payments) {
  return [...(payments || [])]
    .filter((p) => p && !p.deleted)
    .sort((a, b) => {
      const dateA = a.transaction_date || a.created_at || '';
      const dateB = b.transaction_date || b.created_at || '';
      if (dateA !== dateB) return String(dateA).localeCompare(String(dateB));
      return Number(a.id || 0) - Number(b.id || 0);
    });
}

export function buildInvoicePaymentSummary(invoiceTotal, payments) {
  const ledger = sortPaymentsForLedger(payments);
  let totalGross = 0;
  let totalRefunded = 0;
  let totalNet = 0;
  let totalNetConfirmed = 0;

  ledger.forEach((payment) => {
    const { amount, refunded, net } = parsePaymentAmounts(payment);
    totalGross += amount;
    totalRefunded += refunded;
    totalNet += net;
    if (payment.is_confirmed === true) {
      totalNetConfirmed += net;
    }
  });

  const remaining = invoiceTotal - totalNet;
  const balanceDue = remaining > 0 ? remaining : 0;
  const overpaid = remaining < 0 ? Math.abs(remaining) : 0;
  const isPaidInFull = balanceDue === 0 && overpaid === 0 && invoiceTotal > 0 && totalNet >= invoiceTotal;
  const hasPayments = ledger.length > 0;

  return {
    ledger,
    totalGross,
    totalRefunded,
    totalNet,
    totalNetConfirmed,
    balanceDue,
    overpaid,
    isPaidInFull,
    hasPayments,
    hasRefunds: totalRefunded > 0,
  };
}

export function formatPaymentTransactionDate(payment, dateLocale, emDash, { dateOnly = false } = {}) {
  const txDate = payment?.transaction_date;
  const createdAt = payment?.created_at;

  if (dateOnly) {
    const raw = txDate || createdAt;
    if (!raw) return emDash;
    if (txDate && isDateOnlyString(txDate)) {
      return formatLocalDateOnly(txDate);
    }
    return formatDocDateTime(raw, dateLocale, emDash);
  }

  // Date-only transaction_date has no time — use created_at to avoid UTC midnight (4:00 AM in +04:00).
  let raw;
  if (txDate && !isDateOnlyString(txDate)) {
    raw = txDate;
  } else if (createdAt) {
    raw = createdAt;
  } else if (txDate) {
    return formatLocalDateOnly(txDate);
  } else {
    return emDash;
  }

  return formatDocDateTime(raw, dateLocale, emDash);
}

export function getPaymentTransactionStatusLabel(payment, t) {
  const { isPartialRefund, isFullRefund } = parsePaymentAmounts(payment);
  const confirmed = payment?.is_confirmed === true;
  const parts = [];

  if (confirmed) {
    parts.push(t('pdfDocuments.invoice.txStatusConfirmed'));
  }

  if (isFullRefund) {
    parts.push(t('pdfDocuments.invoice.txStatusFullRefund'));
  } else if (isPartialRefund) {
    parts.push(t('pdfDocuments.invoice.txStatusPartialRefund'));
  }

  return parts.join(' · ');
}
